import { findShare, shareUrl, type Share, type ShareTarget } from '@closetly/core';
import { useState } from 'react';
import { useItems } from '../lib/queries';
import { useReservations, useShareActions, useShares, type ShareFields } from '../lib/shares';
import { useToast } from '../lib/toast';
import { useOnline } from '../lib/useOnline';
import { Sheet } from './Sheet';

const WHAT: Record<ShareTarget['kind'], string> = {
  board: 'das Board',
  outfit: 'das Outfit',
  list: 'deine offene Wunschliste',
};

/** Link for a board, an outfit or the whole list: create, set what it shows, stop. */
export function ShareSheet({ uid, target, onClose }: { uid: string; target: ShareTarget; onClose: () => void }) {
  const shares = useShares(uid);
  const actions = useShareActions(uid);
  const online = useOnline();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // options for a link that doesn't exist yet
  const [draft, setDraft] = useState({ show_prices: true, show_notes: false });

  const share = shares.data ? findShare(shares.data, target) : undefined;
  const title = target.kind === 'list' ? 'Wunschliste teilen' : `„${target.name || 'Outfit'}“ teilen`;

  async function run(action: () => Promise<unknown>, done?: string) {
    setBusy(true);
    try {
      await action();
      if (done) toast(done);
    } catch {
      toast(online ? 'Das hat nicht geklappt. Versuch es nochmal.' : 'Dafür braucht es eine Verbindung.');
    } finally {
      setBusy(false);
    }
  }

  const create = () =>
    run(
      () =>
        actions.create({
          ...draft,
          ...(target.kind === 'board' ? { board: target.id } : target.kind === 'outfit' ? { outfit: target.id } : {}),
        }),
      'Link erstellt',
    );
  const set = (fields: ShareFields) => share && run(() => actions.update(share.id, fields));

  return (
    <Sheet open onClose={onClose} onSubmit={onClose} labelledBy="shareTitle">
      <h2 id="shareTitle">{title}</h2>
      <p className="hint">
        Wer den Link hat, sieht {WHAT[target.kind]} ohne Anmeldung, mit Fotos und Links zum Shop. Teile lassen sich
        reservieren, damit nichts doppelt geschenkt wird.
      </p>

      {shares.isPending ? (
        <p className="hint">Lade…</p>
      ) : !share ? (
        <>
          <Options value={draft} disabled={busy} onChange={(f) => setDraft((d) => ({ ...d, ...f }))} />
          <div className="push-actions">
            <button type="button" className="btn ink" disabled={busy || !online} onClick={create}>
              Link erstellen
            </button>
          </div>
          {!online && <p className="hint">Dafür braucht es eine Verbindung.</p>}
        </>
      ) : share.active ? (
        <ActiveLink share={share} busy={busy || !online} onChange={set} uid={uid} />
      ) : (
        <>
          <p className="hint">Der Link ist gestoppt. Wer ihn öffnet, sieht nichts.</p>
          <div className="push-actions">
            <button
              type="button"
              className="btn ink"
              disabled={busy || !online}
              onClick={() => set({ active: true })}
            >
              Wieder freigeben
            </button>
          </div>
        </>
      )}

      <div className="sheet-actions">
        <div className="right">
          <button type="submit" className="btn">
            Fertig
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function Options({
  value,
  disabled,
  onChange,
}: {
  value: Pick<Share, 'show_prices' | 'show_notes'>;
  disabled: boolean;
  onChange: (f: ShareFields) => void;
}) {
  return (
    <>
      <label className="check">
        <input
          type="checkbox"
          checked={value.show_prices}
          disabled={disabled}
          onChange={(e) => onChange({ show_prices: e.target.checked })}
        />{' '}
        Preise zeigen
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={value.show_notes}
          disabled={disabled}
          onChange={(e) => onChange({ show_notes: e.target.checked })}
        />{' '}
        Notizen zeigen
      </label>
    </>
  );
}

function ActiveLink({
  share,
  busy,
  uid,
  onChange,
}: {
  share: Share;
  busy: boolean;
  uid: string;
  onChange: (f: ShareFields) => void;
}) {
  const toast = useToast();
  const url = shareUrl(location.origin, share.token);
  const canShare = typeof navigator.share === 'function';

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast('Link kopiert');
    } catch {
      toast('Kopieren ging nicht. Halte den Link gedrückt und kopier ihn von Hand.');
    }
  }

  return (
    <>
      <div className="field">
        <label htmlFor="share-url">Link</label>
        <div className="linkrow">
          <input id="share-url" value={url} readOnly onFocus={(e) => e.target.select()} />
          <button type="button" className="btn" onClick={copy}>
            Kopieren
          </button>
        </div>
      </div>
      {canShare && (
        <div className="push-actions">
          <button
            type="button"
            className="btn ink"
            onClick={() => navigator.share({ url }).catch(() => {})}
          >
            Teilen…
          </button>
        </div>
      )}

      <Options value={share} disabled={busy} onChange={onChange} />
      <label className="check">
        <input
          type="checkbox"
          checked={share.show_reservations}
          disabled={busy}
          onChange={(e) => onChange({ show_reservations: e.target.checked })}
        />{' '}
        Reservierungen sehen
      </label>
      {share.show_reservations ? (
        <ReservationList uid={uid} share={share} />
      ) : (
        <p className="hint">Bleibt aus, damit Geschenke eine Überraschung bleiben.</p>
      )}

      <div className="push-actions">
        <button type="button" className="btn danger" disabled={busy} onClick={() => onChange({ active: false })}>
          Link stoppen
        </button>
      </div>
    </>
  );
}

function ReservationList({ uid, share }: { uid: string; share: Share }) {
  const reservations = useReservations(share.id);
  const items = useItems(uid).data ?? [];
  const list = reservations.data ?? [];
  if (reservations.isPending) return <p className="hint">Lade…</p>;
  if (list.length === 0) return <p className="hint">Noch nichts reserviert.</p>;
  return (
    <ul className="reserved-list">
      {list.map((r) => (
        <li key={r.id}>
          <b>{items.find((i) => i.id === r.item)?.name ?? 'Ein Teil'}</b>
          <span>{r.name ? `von ${r.name}` : 'ohne Namen'}</span>
        </li>
      ))}
    </ul>
  );
}
